import type { FeedsStatus } from '@ofmos/shared/feeds';
import { useState } from 'react';
import { Button, cx, InlineNotice } from '../../../ui';
import { clockTime, type DataAgeSource, dataAgeOf, staleTelegramSentence } from './dataAge';
import { useCanRetryTouch, useFeedsStatus, useRetryTouch } from './hooks';

/**
 * THE DATA-AGE LINE AND THE STALE TELEGRAM NOTICE (redesign register D9, D10, R47, R48, R-K1;
 * brief FE-FOUNDATION sections 7 and 11; components page section 9).
 *
 * `DataAgeLine` is one line in the meta size, "Telegram read 3 hours ago", and in the late tone
 * when its source is stale (R-K1). It reads the shared feed status unless a `status` is given
 * (the states page draws its own); a source that never ran draws nothing.
 *
 * `StaleTelegramNotice` is the warning band, shown only when Telegram is stale and has
 * succeeded at least once: "Telegram has not been read since 05:12, 13 hours ago. Colours and
 * days may be out of date." "Retry now" is there only for a person holding `HEALTH_MANAGE`
 * (D10); it queues one touch run, shows the button's spinner while the request runs and then
 * re-reads the feed status - it never claims the run happened (record 10). A failed retry is
 * said in the notice itself, "The retry at 05:30 failed too.", after its sentence (decision page
 * item 10.1 answered A, Q-FND-2: a toast disappears, the notice stays); the server's own
 * message is not shown (DESIGN.md D13). A later retry that is accepted takes the sentence away.
 */
export function DataAgeLine({
  source,
  status,
  now,
  className,
}: {
  readonly source: DataAgeSource;
  /** Drawn from this answer instead of the shared read (the states page). */
  readonly status?: FeedsStatus | undefined;
  readonly now?: Date | undefined;
  readonly className?: string | undefined;
}) {
  const shared = useFeedsStatus();
  const answer = status ?? shared.data;
  if (answer === undefined) return null;
  const age = dataAgeOf(source, answer, now ?? new Date());
  if (age === null) return null;
  return (
    <span
      data-data-age={source}
      data-stale={age.stale ? 'true' : 'false'}
      className={cx(
        'text-2xs',
        age.stale ? 'text-(--color-status-late)' : 'text-ink-muted',
        className,
      )}
    >
      {age.text}
    </span>
  );
}

export function StaleTelegramNotice({
  status,
  now,
  canRetry,
  className,
}: {
  readonly status?: FeedsStatus | undefined;
  readonly now?: Date | undefined;
  /** Overrides the session's own flag (the states page draws both). */
  readonly canRetry?: boolean | undefined;
  readonly className?: string | undefined;
}) {
  const shared = useFeedsStatus();
  const allowed = useCanRetryTouch();
  const retry = useRetryTouch();
  const [failedAt, setFailedAt] = useState<string | null>(null);
  const answer = status ?? shared.data;
  if (answer === undefined) return null;
  const sentence = staleTelegramSentence(answer, now ?? new Date());
  if (sentence === null) return null;
  const showRetry = canRetry ?? allowed;
  return (
    <InlineNotice
      tone="warning"
      className={className}
      action={
        showRetry ? (
          <Button
            variant="text"
            loading={retry.isPending}
            onClick={() =>
              retry.mutate(undefined, {
                onSuccess: () => setFailedAt(null),
                onError: () => setFailedAt((now ?? new Date()).toISOString()),
              })
            }
            data-retry-now=""
          >
            Retry now
          </Button>
        ) : undefined
      }
    >
      {failedAt === null ? sentence : `${sentence} The retry at ${clockTime(failedAt)} failed too.`}
    </InlineNotice>
  );
}
